import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Cookies from "js-cookie";

import { useBlogArticleSelectors } from "../store/blogArticle/blogArticle.selectors";
import { useActiveLanguages } from "../store/languages/language.selectors";

import { getHtmlDirection } from "../utils/helpers";

import { useDirection } from "../DirectionProvider";

export const useLang = () => {
  const { i18n } = useTranslation();
  const { lang: langParam, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useBlogArticleSelectors();

  const { languages } = useActiveLanguages();

  const { dir, setDir } = useDirection();

  const updateDirection = useCallback(
    newLang => {
      const htmlDir = getHtmlDirection(languages, newLang);
      if (htmlDir !== dir) {
        setDir(htmlDir);
      }
    },
    [languages, dir, setDir],
  );

  const updateUrl = useCallback(
    newLang => {
      const pathnameSplit = location.pathname.split("/");
      const isNotLangUrl = pathnameSplit[1].length > 2;
      const rootEn = newLang === "en" ? "" : `/${newLang}`;

      if (slug) {
        const linkToFetch = data?.languageCodeSlugs?.[newLang];
        const newUrl = `${rootEn}/blog/${linkToFetch}/`;
        navigate(
          {
            pathname: newUrl,
            hash: location.hash,
            search: location.search,
            state: location.state,
          },
          { replace: true },
        );
      } else {
        navigate(
          {
            pathname: `${rootEn}/${location.pathname.slice(isNotLangUrl ? 1 : 4)}`,
            hash: location.hash,
            search: location.search,
            state: location.state,
          },
          { replace: true },
        );
      }
    },
    [location, navigate, slug, data],
  );

  const changeLanguage = useCallback(
    newLang => async () => {
      try {
        await i18n.changeLanguage(newLang);
        Cookies.set("lang", newLang, { expires: 7 });
        updateDirection(newLang);
        updateUrl(newLang);
      } catch (error) {
        console.error("Error changing language:", error);
      }
    },
    [i18n, updateDirection, updateUrl],
  );

  const normalizeLanguageCode = useCallback(() => {
    const languageCode = i18n.language;
    const parts = languageCode.split("-");
    return parts[0];
  }, [i18n.language]);

  return {
    langParam,
    changeLanguage,
    normalizeLanguageCode,
  };
};
